import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    Sparkles, Bot, Shield, ArrowRight, CheckCircle2, Clock,
    Plus, ChevronRight, Check, AlertTriangle, ShieldCheck,
    Cpu, Globe, Terminal, Play, Zap, RefreshCw, X, Radio,
    FileCode, Sliders, ArrowUpRight, BarChart2, Search,
    Filter, Lock, Server, Users, ShieldAlert, Upload,
    BookOpen, Layers, GitBranch, Edit3, Save, Eye, FileText,
    Folder, ChevronDown, CheckSquare, Trash2, ArrowLeft
} from "lucide-react";

export default function SkillEditor() {
    const { skillId } = useParams();
    const navigate = useNavigate();
    const [skill, setSkill] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editorTab, setEditorTab] = useState("edit"); // "edit" | "preview"
    const [toast, setToast] = useState(null);

    // Form state
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [tags, setTags] = useState(["OKTA", "IDENTITY", "BRUTE-FORCE"]);
    const [markdownContent, setMarkdownContent] = useState("");
    const [enabled, setEnabled] = useState(true);
    const [scoping, setScoping] = useState({
        modules: ["Investigation", "Threat Hunt"],
        sourceProvider: "Okta Identity Engine",
        alertType: "Okta: Suspicious Activity",
        alertCategory: "Credential Access",
        entityConditions: [
            "User.Department != 'Automated Service'",
            "TargetUser.RiskLevel == 'High'"
        ],
        referenceInsights: [
            "ASN Threat Intelligence Feed",
            "Executive VIP User List"
        ]
    });

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchSkillDetail();
    }, [skillId]);

    const fetchSkillDetail = async () => {
        setLoading(true);
        try {
            const targetId = skillId || "sk-okta-bruteforce";
            const res = await axios.get(`${backendURL}/api/skills/${targetId}`, { headers });
            const data = res.data;
            setSkill(data);
            setTitle(data.name || "");
            setDescription(data.description || "");
            setTags(data.tags || ["OKTA", "IDENTITY", "BRUTE-FORCE"]);
            setMarkdownContent(data.markdownContent || "");
            setEnabled(data.enabled !== undefined ? data.enabled : true);
            if (data.scoping) setScoping(data.scoping);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load skill details:", err);
        }
        setLoading(false);
    };

    const handleSave = async (isPublish = false) => {
        try {
            const payload = {
                name: title,
                description,
                tags,
                markdownContent,
                enabled,
                scoping
            };
            const targetId = skillId || "sk-okta-bruteforce";
            const res = await axios.put(`${backendURL}/api/skills/${targetId}`, payload, { headers });
            if (res.data.success) {
                setToast(isPublish ? "Skill Published & Deployed to Live Pipeline!" : "Skill Draft Saved Successfully");
                setTimeout(() => setToast(null), 3000);
            }
        } catch (err) {
            console.error("Failed to save skill:", err);
        }
    };

    const toggleModule = (moduleName) => {
        setScoping(prev => {
            const exists = prev.modules.includes(moduleName);
            return {
                ...prev,
                modules: exists
                    ? prev.modules.filter(m => m !== moduleName)
                    : [...prev.modules, moduleName]
            };
        });
    };

    const addEntityCondition = () => {
        const cond = prompt("Enter new entity condition (e.g. Asset.Criticality == 'Crown Jewel'):");
        if (cond && cond.trim()) {
            setScoping(prev => ({
                ...prev,
                entityConditions: [...prev.entityConditions, cond.trim()]
            }));
        }
    };

    const removeEntityCondition = (idx) => {
        setScoping(prev => ({
            ...prev,
            entityConditions: prev.entityConditions.filter((_, i) => i !== idx)
        }));
    };

    const addReferenceInsight = () => {
        const insight = prompt("Enter reference context name (e.g. Threat Intel IOC Feed):");
        if (insight && insight.trim()) {
            setScoping(prev => ({
                ...prev,
                referenceInsights: [...prev.referenceInsights, insight.trim()]
            }));
        }
    };

    const removeReferenceInsight = (idx) => {
        setScoping(prev => ({
            ...prev,
            referenceInsights: prev.referenceInsights.filter((_, i) => i !== idx)
        }));
    };

    return (
        <div className="w-full space-y-6 pb-12">
            {/* ─── Toast Feedback ─── */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 bg-purple-600 text-white px-4 py-2.5 rounded-xl shadow-2xl text-[12px] font-bold flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-200">
                    <Check className="w-4 h-4" />
                    {toast}
                </div>
            )}

            {/* ─── Header & Navigation Ribbon ─── */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/[0.06] pb-4">
                <div className="flex items-center gap-3">
                    <Link
                        to="/skills"
                        className="p-1.5 bg-black/40 hover:bg-white/[0.08] text-slate-400 hover:text-white rounded-lg transition-colors"
                        title="Back to Skills"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </Link>
                    <div className="flex items-center gap-2 text-[12px] font-mono text-slate-500">
                        <Link to="/skills" className="hover:text-purple-400">Skills</Link>
                        <span>/</span>
                        <span className="text-white font-bold">{title || "Skill Strategy Editor"}</span>
                    </div>

                    <button
                        onClick={() => setEnabled(!enabled)}
                        className={`w-8 h-4.5 rounded-full transition-colors relative p-0.5 ml-2 focus:outline-none ${
                            enabled ? "bg-purple-600" : "bg-slate-700"
                        }`}
                    >
                        <div
                            className={`w-3.5 h-3.5 rounded-full bg-white transition-transform ${
                                enabled ? "translate-x-3.5" : "translate-x-0"
                            }`}
                        />
                    </button>
                </div>

                <div className="flex items-center gap-2 text-[11.5px]">
                    <span className="text-slate-500 font-mono hidden md:inline">
                        Author: secops.team@querytel.com • Last modified: {skill?.lastUpdated || "Jul 02, 2026"}
                    </span>
                    <button
                        onClick={() => handleSave(false)}
                        className="px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg font-medium"
                    >
                        Save Draft
                    </button>
                    <button
                        onClick={() => handleSave(true)}
                        className="flex items-center gap-1.5 px-3.5 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-lg font-bold shadow-md shadow-purple-600/20"
                    >
                        <Zap className="w-3.5 h-3.5 fill-current" />
                        Publish Skill
                    </button>
                </div>
            </div>

            {/* ─── Skill Properties Top Card (Matching Screenshot 02) ─── */}
            <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-xl space-y-3">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <div className="lg:col-span-8 space-y-3">
                        <div>
                            <label className="text-[10px] font-mono text-slate-500 uppercase block mb-1">
                                Skill Title
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-3.5 py-2 text-[14px] font-bold text-white outline-none focus:border-purple-500"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-mono text-slate-500 uppercase block mb-1">
                                Strategy Summary
                            </label>
                            <input
                                type="text"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-3.5 py-2 text-[12px] text-slate-300 outline-none focus:border-purple-500"
                            />
                        </div>
                    </div>

                    <div className="lg:col-span-4 space-y-3">
                        <label className="text-[10px] font-mono text-slate-500 uppercase block mb-1">
                            Tags & Categories
                        </label>
                        <div className="flex flex-wrap gap-1.5 pt-1">
                            {tags.map((tag, idx) => (
                                <span
                                    key={idx}
                                    className="px-2 py-1 bg-purple-500/15 border border-purple-500/30 text-purple-300 rounded-lg text-[10px] font-mono font-bold"
                                >
                                    {tag}
                                </span>
                            ))}
                            <button
                                onClick={() => {
                                    const newTag = prompt("Enter tag name:");
                                    if (newTag && newTag.trim()) setTags([...tags, newTag.trim().toUpperCase()]);
                                }}
                                className="px-2 py-1 bg-white/[0.04] hover:bg-white/[0.08] text-slate-400 hover:text-white rounded-lg text-[10px] font-mono"
                            >
                                + Add Tag
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── 3-PANE SPLIT LAYOUT IDE ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
                
                {/* ═══ PANE 1: File & Asset Tree (2.5 cols) ═══ */}
                <div className="lg:col-span-3 bg-[#111827] border border-white/[0.06] rounded-2xl p-4 shadow-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-white/[0.06] pb-2.5">
                        <span className="text-[11px] font-bold text-white uppercase tracking-wider font-mono flex items-center gap-1.5">
                            <Folder className="w-3.5 h-3.5 text-purple-400" />
                            Skill Files
                        </span>
                    </div>

                    <div className="space-y-1 text-[11.5px] font-mono">
                        {/* SKILL.md */}
                        <div className="flex items-center gap-2 p-2 rounded-xl bg-purple-600/20 text-white border border-purple-500/30 cursor-pointer">
                            <FileText className="w-3.5 h-3.5 text-purple-300" />
                            <span className="font-bold">SKILL.md</span>
                        </div>

                        {/* References */}
                        <div className="pt-2 text-[10px] uppercase text-slate-500 font-bold px-1">
                            References
                        </div>
                        <div className="flex items-center gap-2 p-1.5 rounded-lg text-slate-300 hover:bg-white/[0.02] cursor-pointer">
                            <FileCode className="w-3.5 h-3.5 text-blue-400" />
                            <span>okta_baseline.json</span>
                        </div>

                        {/* Checklists */}
                        <div className="pt-2 text-[10px] uppercase text-slate-500 font-bold px-1">
                            Checklists
                        </div>
                        <div className="flex items-center gap-2 p-1.5 rounded-lg text-slate-300 hover:bg-white/[0.02] cursor-pointer">
                            <CheckSquare className="w-3.5 h-3.5 text-emerald-400" />
                            <span>triage_checklist.md</span>
                        </div>
                    </div>

                    <div className="pt-3 border-t border-white/[0.06] space-y-1.5">
                        <button
                            onClick={() => setToast("Added reference file slot")}
                            className="w-full text-left px-2.5 py-1.5 rounded-lg bg-black/30 hover:bg-white/[0.04] text-[10.5px] text-slate-400 hover:text-white transition-colors"
                        >
                            + Add Reference File
                        </button>
                        <button
                            onClick={() => setToast("Added checklist slot")}
                            className="w-full text-left px-2.5 py-1.5 rounded-lg bg-black/30 hover:bg-white/[0.04] text-[10.5px] text-slate-400 hover:text-white transition-colors"
                        >
                            + Add Checklist
                        </button>
                    </div>
                </div>

                {/* ═══ PANE 2: Markdown Strategy Editor (5.5 cols) ═══ */}
                <div className="lg:col-span-5 bg-[#111827] border border-white/[0.06] rounded-2xl shadow-xl overflow-hidden flex flex-col min-h-[580px]">
                    
                    {/* Editor Header Bar */}
                    <div className="p-3 border-b border-white/[0.06] bg-black/40 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] font-mono text-purple-400 font-bold">SKILL.md</span>
                            <div className="flex items-center bg-[#1E293B] rounded-lg p-0.5 border border-white/[0.06] text-[10.5px]">
                                <button
                                    onClick={() => setEditorTab("edit")}
                                    className={`px-2.5 py-1 rounded-md font-bold transition-colors ${
                                        editorTab === "edit" ? "bg-purple-600 text-white" : "text-slate-400 hover:text-white"
                                    }`}
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => setEditorTab("preview")}
                                    className={`px-2.5 py-1 rounded-md font-bold transition-colors ${
                                        editorTab === "preview" ? "bg-purple-600 text-white" : "text-slate-400 hover:text-white"
                                    }`}
                                >
                                    Preview
                                </button>
                            </div>
                        </div>

                        <span className="text-[10px] font-mono text-slate-500">
                            {markdownContent.split("\n").length} lines • Markdown
                        </span>
                    </div>

                    {/* Editor / Preview Body */}
                    <div className="flex-1 p-4 bg-[#0B0F17]">
                        {editorTab === "edit" ? (
                            <textarea
                                value={markdownContent}
                                onChange={(e) => setMarkdownContent(e.target.value)}
                                className="w-full h-full min-h-[500px] bg-transparent text-slate-200 font-mono text-[12px] leading-relaxed outline-none resize-none custom-scrollbar"
                                placeholder="# Write your AI investigation strategy here in plain markdown..."
                            />
                        ) : (
                            <div className="prose prose-invert max-w-none text-[12px] space-y-3 leading-relaxed">
                                <div className="p-4 bg-black/40 rounded-xl border border-white/[0.04] whitespace-pre-wrap font-sans text-slate-200">
                                    {markdownContent}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ═══ PANE 3: Environmental Scoping & Relevance Rules (4 cols - Screenshot 03) ═══ */}
                <div className="lg:col-span-4 bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-xl space-y-5">
                    <div className="border-b border-white/[0.06] pb-2.5">
                        <h3 className="text-[12px] font-bold text-white uppercase tracking-wider font-mono flex items-center gap-1.5">
                            <Sliders className="w-3.5 h-3.5 text-blue-400" />
                            Environmental Scoping
                        </h3>
                        <p className="text-[10.5px] text-slate-400 mt-1">
                            Defines the precise conditions when QueryTel loads this strategy.
                        </p>
                    </div>

                    {/* Execution Scope */}
                    <div className="space-y-2">
                        <label className="text-[10px] font-mono text-slate-400 uppercase block font-bold">
                            Execution Scope
                        </label>
                        <div className="grid grid-cols-2 gap-2 text-[11px]">
                            {["Investigation", "Threat Hunt", "Copilot Chat"].map((mod) => (
                                <button
                                    key={mod}
                                    type="button"
                                    onClick={() => toggleModule(mod)}
                                    className={`p-2 rounded-xl border text-left flex items-center justify-between transition-all ${
                                        scoping.modules?.includes(mod)
                                            ? "bg-purple-600/20 border-purple-500/40 text-white font-bold"
                                            : "bg-black/30 border-white/[0.04] text-slate-400"
                                    }`}
                                >
                                    <span>{mod}</span>
                                    {scoping.modules?.includes(mod) && <Check className="w-3 h-3 text-purple-400" />}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Trigger Connectors & Sources */}
                    <div className="space-y-3 pt-2 border-t border-white/[0.06]">
                        <label className="text-[10px] font-mono text-slate-400 uppercase block font-bold">
                            Trigger Connectors & Sources
                        </label>

                        <div className="space-y-2 text-[11px]">
                            <div>
                                <span className="text-[10px] text-slate-500 font-mono block mb-1">Source Provider</span>
                                <select
                                    value={scoping.sourceProvider}
                                    onChange={(e) => setScoping({ ...scoping, sourceProvider: e.target.value })}
                                    className="w-full bg-[#1E293B] border border-white/[0.12] rounded-lg px-2.5 py-1.5 text-white outline-none"
                                >
                                    <option value="FortiGate Firewall">FortiGate Firewall</option>
                                    <option value="FortiAnalyzer Core">FortiAnalyzer Core</option>
                                    <option value="Microsoft Defender XDR">Microsoft Defender XDR</option>
                                    <option value="Microsoft Entra ID">Microsoft Entra ID</option>
                                    <option value="ThreatDown / Malwarebytes OneView">ThreatDown / Malwarebytes OneView</option>
                                </select>
                            </div>

                            <div>
                                <span className="text-[10px] text-slate-500 font-mono block mb-1">Alert Type</span>
                                <input
                                    type="text"
                                    value={scoping.alertType}
                                    onChange={(e) => setScoping({ ...scoping, alertType: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-2.5 py-1.5 text-white outline-none"
                                />
                            </div>

                            <div>
                                <span className="text-[10px] text-slate-500 font-mono block mb-1">Alert Category</span>
                                <input
                                    type="text"
                                    value={scoping.alertCategory}
                                    onChange={(e) => setScoping({ ...scoping, alertCategory: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-2.5 py-1.5 text-white outline-none"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Custom Conditions & Filters */}
                    <div className="space-y-2.5 pt-2 border-t border-white/[0.06]">
                        <div className="flex items-center justify-between">
                            <label className="text-[10px] font-mono text-slate-400 uppercase font-bold">
                                Entity Conditions
                            </label>
                            <button
                                onClick={addEntityCondition}
                                className="text-[10px] text-blue-400 hover:text-blue-300 font-bold"
                            >
                                + Add Condition
                            </button>
                        </div>

                        <div className="space-y-1.5">
                            {(scoping.entityConditions || []).map((cond, idx) => (
                                <div key={idx} className="p-2 bg-black/40 border border-white/[0.04] rounded-lg flex items-center justify-between text-[10.5px] font-mono text-slate-300">
                                    <span>{cond}</span>
                                    <button onClick={() => removeEntityCondition(idx)} className="text-slate-500 hover:text-rose-400">
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Reference Contexts */}
                    <div className="space-y-2.5 pt-2 border-t border-white/[0.06]">
                        <div className="flex items-center justify-between">
                            <label className="text-[10px] font-mono text-slate-400 uppercase font-bold">
                                Reference Context
                            </label>
                            <button
                                onClick={addReferenceInsight}
                                className="text-[10px] text-purple-400 hover:text-purple-300 font-bold"
                            >
                                + Add Context
                            </button>
                        </div>

                        <div className="space-y-1.5">
                            {(scoping.referenceInsights || []).map((ref, idx) => (
                                <div key={idx} className="p-2 bg-black/40 border border-white/[0.04] rounded-lg flex items-center justify-between text-[10.5px] font-mono text-purple-300">
                                    <span>{ref}</span>
                                    <button onClick={() => removeReferenceInsight(idx)} className="text-slate-500 hover:text-rose-400">
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>

            </div>
        </div>
    );
}
